// Notes, decision 90 v2 (Carla, 2026-10-12): what the Notes page needs to know about a note, in one
// place, so the page, its cards and its tests cannot disagree.
import { blocksToText } from './BlockEditor';

// ── Is there anything in it? ("A new note left with no title and no content is discarded.") ──────
// The server's copy is "Aidey Backend/lib/noteContent.js". The app is STRICTER on one point: a picture
// block whose upload has not finished (no file id, no src) is not content here, so a note left with
// only a failed picture is discarded; the server accepts that block so the picture can be stored.
const stripTags = (html) => {
  if (!html) return '';
  const d = document.createElement('div');
  d.innerHTML = html;
  return (d.textContent || '').replace(/ /g, ' ').trim();
};
export function blockHasContent(b) {
  if (!b || typeof b !== 'object') return false;
  if (b.type === 'page') return !!b.pageId;
  if (b.type === 'image') return !!(b.fileId || b.src);
  if (b.type === 'divider') return false;
  return stripTags(b.html).length > 0;
}
export function noteHasContent(n) {
  if (!n) return false;
  if (String(n.title || '').trim()) return true;
  if (Array.isArray(n.blocks) && n.blocks.length) return n.blocks.some(blockHasContent);
  return !!String(n.text || '').trim();
}

// ── Its name and its preview ───────────────────────────────────────────────────────────────────
function bodyLines(note) {
  const fromBlocks = Array.isArray(note?.blocks) && note.blocks.length ? blocksToText(note.blocks) : '';
  const body = fromBlocks.trim() ? fromBlocks : (note?.text || '');
  return body.split('\n').map((s) => s.replace(/\s+/g, ' ').trim()).filter(Boolean);
}
export function noteTitle(note) {
  if (note?.title?.trim()) return note.title.trim();
  const first = bodyLines(note)[0];
  return first ? first.split(' ').slice(0, 8).join(' ') : 'Untitled';
}
export function notePreview(note, max = 220) {
  const lines = bodyLines(note);
  const rest = note?.title?.trim() ? lines : lines.slice(1);
  const s = rest.join(' ');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// ── Categories: the built-in five, which a category of the person's own with the same name overrides ──
// Nothing is seeded into anyone's account: a built-in with no row of its own is a chip in code. Filing
// a note under one makes the person's own category of that name then, and so does renaming or
// recolouring it in Manage categories (the category manager, Carla 2026-10-12); that row stays the
// built-in when renamed, because the server records which row a built-in became.
//   General   notes with no category (and a category the person named General); never renamed or deleted
//   Meetings  also Aidey's meeting notes that have no category (lib/aideyMeetingNote files them in the
//             category that IS Meetings, renamed or not; once Meetings is deleted, in General)
export const BUILT_IN = [
  { key: 'general', name: 'General', colour: 'pastel-neutral' },
  { key: 'meetings', name: 'Meetings', colour: 'pastel-blue' },
  { key: 'clients', name: 'Clients', colour: 'pastel-mint' },
  { key: 'projects', name: 'Projects', colour: 'pastel-lilac' },
  { key: 'ideas', name: 'Ideas', colour: 'pastel-yellow' },
];
const norm = (s) => String(s || '').trim().toLowerCase();

// The chips, in the person's order: { key, name, colour (a colour or null), token, catId|null, builtIn|null }.
//   cats    her rows; the server says on each which built-in it is (builtIn: key | null)
//   layout  { order: [keys], hidden: [built-in keys she deleted] } from GET /api/note-categories
// The same rule as the server's ("Aidey Backend/lib/noteCategoryLayout.js" resolveBindings): a built-in
// is the row the server bound to it, else her earliest row with its name not bound to another; a
// deleted built-in is not shown, and a row with its name is then an ordinary category.
export function categoryChips(cats, layout) {
  const own = cats || [];
  const hidden = new Set((layout && layout.hidden) || []);
  const used = new Set();
  const rowFor = {};
  for (const b of BUILT_IN) {
    if (hidden.has(b.key) && b.key !== 'general') continue;
    const row = own.find((c) => c.builtIn === b.key && !used.has(c.id));
    if (row) { rowFor[b.key] = row; used.add(row.id); }
  }
  for (const b of BUILT_IN) {
    if ((hidden.has(b.key) && b.key !== 'general') || rowFor[b.key]) continue;
    const row = own.find((c) => !used.has(c.id) && !c.builtIn && norm(c.name) === norm(b.name));
    if (row) { rowFor[b.key] = row; used.add(row.id); }
  }
  const builtIns = BUILT_IN.filter((b) => b.key === 'general' || !hidden.has(b.key)).map((b) => {
    const row = rowFor[b.key];
    return { key: b.key, name: row ? row.name : b.name, colour: row ? row.colour : null, token: b.colour, catId: row ? row.id : null, builtIn: b.key };
  });
  const rest = own.filter((c) => !used.has(c.id)).map((c) => ({ key: `cat:${c.id}`, name: c.name, colour: c.colour, token: null, catId: c.id, builtIn: null }));
  const all = [...builtIns, ...rest];
  const order = (layout && layout.order) || [];
  if (!order.length) return all;
  const at = new Map(order.map((k, i) => [k, i]));
  return all.map((c, i) => ({ c, i })).sort((a, b) => {
    const pa = at.has(a.c.key) ? at.get(a.c.key) : order.length + a.i;
    const pb = at.has(b.c.key) ? at.get(b.c.key) : order.length + b.i;
    return pa - pb;
  }).map((x) => x.c);
}

// Which chip a note sits under.
export function chipKeyOf(note, chips) {
  const byCat = note?.categoryId ? chips.find((c) => c.catId && c.catId === note.categoryId) : null;
  if (byCat) return byCat.key;
  if (note?.categoryId) return null;   // a category that is gone or not loaded: under All only
  if (note?.source === 'aidey_meeting' && chips.some((c) => c.key === 'meetings')) return 'meetings';
  return 'general';
}

// ── The note's address (decision 90: "the address reflects it, so Back, reload and links work") ──
export const NOTES_PATH = '/notes';
export const pathForNote = (id) => `${NOTES_PATH}/${encodeURIComponent(id)}`;
export function noteIdFromPath(pathname) {
  const m = /^\/notes\/([^/]+)\/?$/.exec(String(pathname || ''));
  if (!m) return null;
  try { return decodeURIComponent(m[1]); } catch { return null; }
}

// ── Months (the navigator) ────────────────────────────────────────────────────────────────────
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const editedAt = (n) => n?.updated || n?.created || null;
export function monthKeyOf(iso) {
  const d = new Date(iso || '');
  return Number.isNaN(d.getTime()) ? null : `${d.getFullYear()}-${d.getMonth()}`;
}

// ── "+ New note" from a client, a project or a task: the link to pre-fill ──────────────────────
// Carried in the deep link ?note_new=contact:<id> | project:<id> | task:<id> (utils/deepLink).
export function linkFromIntent(s) {
  const m = /^(contact|project|task):(.+)$/.exec(String(s || ''));
  if (!m) return null;
  if (m[1] === 'contact') return { contactId: m[2] };
  if (m[1] === 'project') return Number.isInteger(Number(m[2])) ? { projectId: Number(m[2]) } : null;
  return /^\d+$/.test(m[2]) ? { taskId: m[2] } : null;
}
export const intentFor = (kind, id) => `${kind}:${id}`;
